'use client';

import { useState, useRef, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { askAboutCharacter, extractBrainstormUpdates, patchCharacterFields } from '@/lib/actions';
import { Bot, Send, Sparkles, Wand2, ArrowLeft, Check, CheckSquare, Square } from 'lucide-react';
import MarkdownRenderer from './MarkdownRenderer';

interface CustomField { label: string; value: string; }

interface Character {
  name: string;
  background?: string;
  personality?: string;
  abilities?: string;
  custom_fields?: string;
}

interface Props {
  characterId: string;
  character: Character;
}

type CommitState = 'idle' | 'extracting' | 'reviewing' | 'saving' | 'saved';

interface Proposals {
  background?: string;
  personality?: string;
  abilities?: string;
  custom_fields?: CustomField[];
}

const FIELD_META: { key: keyof Omit<Proposals, 'custom_fields'>; label: string; color: string }[] = [
  { key: 'background',  label: 'Background',  color: 'text-purple-400' },
  { key: 'personality', label: 'Personality',  color: 'text-amber-400'  },
  { key: 'abilities',   label: 'Abilities',    color: 'text-emerald-400' },
];

export default function CharacterBrainstorm({ characterId, character }: Props) {
  const router = useRouter();
  const [messages, setMessages] = useState<{ role: string; content: string }[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Commit flow state
  const [commitState, setCommitState] = useState<CommitState>('idle');
  const [proposals, setProposals] = useState<Proposals>({});
  const [edited, setEdited] = useState<Proposals>({});
  const [checkedFields, setCheckedFields] = useState<Set<string>>(new Set());

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // ── Chat ──────────────────────────────────────────────────────────────────

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMsg = { role: 'user', content: input };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const response = await askAboutCharacter(characterId, input);
      setMessages(prev => [...prev, { role: 'bot', content: response }]);
    } catch {
      setMessages(prev => [...prev, { role: 'bot', content: "The Oracle is silent. Check the server connection!" }]);
    } finally {
      setLoading(false);
    }
  };

  // ── Commit flow ───────────────────────────────────────────────────────────

  const handleStartCommit = async () => {
    setCommitState('extracting');
    try {
      const result = await extractBrainstormUpdates(characterId, messages);
      const p = result as Proposals;
      const hasContent =
        p.background || p.personality || p.abilities ||
        (p.custom_fields && p.custom_fields.length > 0);

      if (!hasContent) {
        // Nothing useful found — pop back to chat with a system message
        setMessages(prev => [
          ...prev,
          {
            role: 'bot',
            content: "Hmm, I couldn't find any specific details to save from our chat yet. Try asking me to describe this character's history, personality, or powers — then we can save the best ideas!",
          },
        ]);
        setCommitState('idle');
        return;
      }

      // Pre-check all fields that have content
      const initialChecked = new Set<string>();
      if (p.background)  initialChecked.add('background');
      if (p.personality) initialChecked.add('personality');
      if (p.abilities)   initialChecked.add('abilities');
      p.custom_fields?.forEach((_, i) => initialChecked.add(`cf_${i}`));

      setProposals(p);
      setEdited({
        background:    p.background,
        personality:   p.personality,
        abilities:     p.abilities,
        custom_fields: p.custom_fields,
      });
      setCheckedFields(initialChecked);
      setCommitState('reviewing');
    } catch {
      setCommitState('idle');
    }
  };

  const toggleField = (key: string) => {
    setCheckedFields(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const handleConfirm = async () => {
    setCommitState('saving');

    const patches: Parameters<typeof patchCharacterFields>[1] = {};
    if (checkedFields.has('background') && edited.background)   patches.background  = edited.background;
    if (checkedFields.has('personality') && edited.personality) patches.personality = edited.personality;
    if (checkedFields.has('abilities') && edited.abilities)     patches.abilities   = edited.abilities;

    const newCF = (edited.custom_fields || []).filter((_, i) => checkedFields.has(`cf_${i}`));
    if (newCF.length > 0) patches.new_custom_fields = newCF;

    await patchCharacterFields(characterId, patches);
    setCommitState('saved');
    setTimeout(() => {
      setCommitState('idle');
      router.refresh();
    }, 1800);
  };

  const noneChecked = checkedFields.size === 0;

  // ── Render helpers ────────────────────────────────────────────────────────

  const renderChat = () => (
    <>
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {messages.length === 0 && (
          <div className="h-full flex flex-col items-center justify-center text-center max-w-xs mx-auto opacity-40">
            <Bot size={48} className="mb-4 text-purple-500" />
            <p className="text-sm italic">"Ask me to help expand this character's history, suggest new abilities, or describe their deepest fears."</p>
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} className={`flex gap-3 ${m.role === 'user' ? 'justify-end' : ''}`}>
            {m.role === 'bot' && (
              <div className="w-8 h-8 rounded-full bg-purple-900 flex items-center justify-center border border-purple-500 shrink-0 mt-1">
                <Bot size={14} />
              </div>
            )}
            <div className={`p-3 rounded-2xl text-sm max-w-[85%] ${m.role === 'user' ? 'bg-purple-600 text-white' : 'bg-slate-800 border border-slate-700'}`}>
              {m.role === 'bot' ? <MarkdownRenderer content={m.content} /> : m.content}
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex gap-3 animate-pulse">
            <div className="w-8 h-8 rounded-full bg-purple-900/50 shrink-0" />
            <div className="bg-slate-800 border border-slate-700 p-3 rounded-2xl w-24 h-8" />
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Apply button — only shown when there's a conversation to work with */}
      {messages.length >= 2 && (
        <div className="px-4 pb-2">
          <button
            onClick={handleStartCommit}
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 py-2 text-xs font-mono uppercase tracking-widest text-purple-300 hover:text-purple-200 border border-purple-500/20 hover:border-purple-500/40 rounded-xl bg-purple-900/10 hover:bg-purple-900/20 transition-all disabled:opacity-40"
          >
            <Wand2 size={14} /> Apply ideas to {character.name}'s profile
          </button>
        </div>
      )}

      <div className="p-4 border-t border-slate-800 bg-slate-900/50 flex gap-2">
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSend()}
          placeholder="Give me an idea for..."
          className="flex-1 bg-slate-950 border border-slate-800 rounded-xl px-4 py-2 text-sm outline-none focus:ring-1 focus:ring-purple-500"
        />
        <button
          onClick={handleSend}
          disabled={loading}
          className="p-2 bg-purple-600 hover:bg-purple-500 rounded-lg text-white disabled:opacity-50"
        >
          <Send size={18} />
        </button>
      </div>
    </>
  );

  const renderExtracting = () => (
    <div className="flex-1 flex flex-col items-center justify-center gap-4 text-center p-8">
      <div className="w-12 h-12 rounded-full bg-purple-900 border border-purple-500 flex items-center justify-center animate-pulse">
        <Sparkles size={22} className="text-purple-400" />
      </div>
      <p className="text-slate-300 font-serif italic">Reviewing your brainstorm...</p>
      <p className="text-slate-500 text-sm">The Oracle is finding the best ideas to save.</p>
    </div>
  );

  const renderReview = () => (
    <>
      <div className="flex-1 overflow-y-auto p-5 space-y-4">
        <div className="flex items-center gap-2 mb-2">
          <button
            onClick={() => setCommitState('idle')}
            className="p-1 text-slate-500 hover:text-slate-300 transition-colors"
          >
            <ArrowLeft size={16} />
          </button>
          <h4 className="text-sm font-mono uppercase tracking-widest text-slate-400">
            Choose what to save
          </h4>
        </div>

        <p className="text-xs text-slate-500 italic pb-1">
          The Oracle pulled these ideas from your brainstorm. Edit anything, uncheck what you don't want, then confirm.
        </p>

        {/* Standard fields */}
        {FIELD_META.map(({ key, label, color }) => {
          if (!proposals[key]) return null;
          const isChecked = checkedFields.has(key);
          return (
            <div
              key={key}
              className={`rounded-xl border transition-colors ${isChecked ? 'border-slate-700 bg-slate-800/50' : 'border-slate-800 bg-slate-900/30 opacity-50'}`}
            >
              <button
                onClick={() => toggleField(key)}
                className="w-full flex items-center gap-3 px-4 pt-3 pb-2 text-left"
              >
                {isChecked
                  ? <CheckSquare size={16} className="shrink-0 text-purple-400" />
                  : <Square size={16} className="shrink-0 text-slate-600" />}
                <span className={`text-xs font-mono uppercase tracking-widest font-bold ${color}`}>{label}</span>
              </button>
              {isChecked && (
                <div className="px-4 pb-3">
                  <textarea
                    value={edited[key] ?? ''}
                    onChange={e => setEdited(prev => ({ ...prev, [key]: e.target.value }))}
                    rows={4}
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-purple-500/50 resize-y"
                  />
                  {character[key] && (
                    <p className="text-xs text-slate-600 italic mt-1">
                      Current: {String(character[key]).slice(0, 80)}{String(character[key]).length > 80 ? '…' : ''}
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* Custom fields from brainstorm */}
        {proposals.custom_fields?.map((cf, i) => {
          const isChecked = checkedFields.has(`cf_${i}`);
          return (
            <div
              key={i}
              className={`rounded-xl border transition-colors ${isChecked ? 'border-slate-700 bg-slate-800/50' : 'border-slate-800 bg-slate-900/30 opacity-50'}`}
            >
              <button
                onClick={() => toggleField(`cf_${i}`)}
                className="w-full flex items-center gap-3 px-4 pt-3 pb-2 text-left"
              >
                {isChecked
                  ? <CheckSquare size={16} className="shrink-0 text-blue-400" />
                  : <Square size={16} className="shrink-0 text-slate-600" />}
                <span className="text-xs font-mono uppercase tracking-widest font-bold text-blue-400">
                  New: {cf.label}
                </span>
              </button>
              {isChecked && (
                <div className="px-4 pb-3">
                  <input
                    value={edited.custom_fields?.[i]?.label ?? cf.label}
                    onChange={e => setEdited(prev => {
                      const cfs = [...(prev.custom_fields ?? proposals.custom_fields ?? [])];
                      cfs[i] = { ...cfs[i], label: e.target.value };
                      return { ...prev, custom_fields: cfs };
                    })}
                    placeholder="Field label"
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-1.5 text-xs text-slate-300 focus:outline-none focus:border-blue-500/50 mb-1.5"
                  />
                  <textarea
                    value={edited.custom_fields?.[i]?.value ?? cf.value}
                    onChange={e => setEdited(prev => {
                      const cfs = [...(prev.custom_fields ?? proposals.custom_fields ?? [])];
                      cfs[i] = { ...cfs[i], value: e.target.value };
                      return { ...prev, custom_fields: cfs };
                    })}
                    rows={3}
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-blue-500/50 resize-y"
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="p-4 border-t border-slate-800 bg-slate-900/50 flex gap-2">
        <button
          onClick={() => setCommitState('idle')}
          className="px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded-xl text-sm text-slate-400 transition-colors"
        >
          Keep chatting
        </button>
        <button
          onClick={handleConfirm}
          disabled={noneChecked}
          className="flex-1 flex items-center justify-center gap-2 py-2 bg-purple-600 hover:bg-purple-500 disabled:opacity-40 disabled:cursor-not-allowed rounded-xl text-sm font-bold transition-colors"
        >
          <Check size={16} /> Apply to {character.name}
        </button>
      </div>
    </>
  );

  const renderSaving = () => (
    <div className="flex-1 flex flex-col items-center justify-center gap-4 text-center p-8">
      <div className="w-12 h-12 rounded-full bg-purple-900 border border-purple-500 flex items-center justify-center animate-pulse">
        <Sparkles size={22} className="text-purple-400" />
      </div>
      <p className="text-slate-300 font-serif italic">Saving to {character.name}'s profile...</p>
    </div>
  );

  const renderSaved = () => (
    <div className="flex-1 flex flex-col items-center justify-center gap-4 text-center p-8">
      <div className="w-14 h-14 rounded-full bg-emerald-900/60 border border-emerald-500/50 flex items-center justify-center">
        <Check size={24} className="text-emerald-400" />
      </div>
      <p className="text-emerald-300 font-serif italic font-bold">Saved!</p>
      <p className="text-slate-500 text-sm">The character profile has been updated.</p>
    </div>
  );

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-3xl overflow-hidden flex flex-col h-[600px] shadow-2xl shadow-purple-950/20">
      <header className="p-4 border-b border-slate-800 bg-slate-900/50 flex items-center gap-3 shrink-0">
        <div className="p-2 bg-purple-600 rounded-lg">
          <Sparkles size={18} className="text-white" />
        </div>
        <h3 className="font-serif font-bold italic text-purple-300">Brainstorm with the Oracle</h3>
      </header>

      {commitState === 'idle'       && renderChat()}
      {commitState === 'extracting' && renderExtracting()}
      {commitState === 'reviewing'  && renderReview()}
      {commitState === 'saving'     && renderSaving()}
      {commitState === 'saved'      && renderSaved()}
    </div>
  );
}
